/**
 * JWT Debugger Component
 * Shows decoded JWT token claims to help debug admin role issues
 */

import { useState } from 'react';
import { IconKey, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { Paper, Text, Stack, Group, Button, Badge, Code } from '../../../../components/ui';
import { useEnrichedAuth } from '../../../../hooks/useEnrichedAuth';

export function JWTDebugger() {
  const { user, hasRole } = useEnrichedAuth();
  const [showDetails, setShowDetails] = useState(false);

  // Decode JWT token (if stored in localStorage)
  const getTokenClaims = () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return null;

      // JWT has 3 parts separated by dots
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      // Decode the payload (middle part)
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch (error) {
      console.error('[JWTDebugger] Failed to decode token:', error);
      return null;
    }
  };

  const claims = getTokenClaims();
  const isAdmin = hasRole('Admin');

  return (
    <Paper p="md" withBorder style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <IconKey size={16} />
            <Text size="sm" fw={600}>
              JWT Token Debug Info
            </Text>
          </Group>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => setShowDetails(!showDetails)}
            rightSection={showDetails ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
          >
            {showDetails ? 'Hide' : 'Show'} Details
          </Button>
        </Group>

        {/* Quick Status */}
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            User ID:
          </Text>
          <Text size="xs" fw={500}>
            {user?.id || 'N/A'}
          </Text>
        </Group>

        <Group gap="xs">
          <Text size="xs" c="dimmed">
            Admin Role:
          </Text>
          <Badge color={isAdmin ? 'green' : 'red'} size="sm">
            {isAdmin ? 'YES' : 'NO'}
          </Badge>
        </Group>

        {/* Roles */}
        {user?.roles && user.roles.length > 0 && (
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              Roles:
            </Text>
            {user.roles.map((role) => (
              <Badge key={role} size="xs" variant="light">
                {role}
              </Badge>
            ))}
          </Group>
        )}

        {/* Detailed Claims */}
        {showDetails && claims && (
          <Stack gap="xs">
            <Text size="xs" fw={600} c="dimmed">
              Raw JWT Claims:
            </Text>
            <Code
              block
              style={{
                fontSize: '10px',
                maxHeight: '300px',
                overflow: 'auto',
              }}
            >
              {JSON.stringify(claims, null, 2)}
            </Code>
          </Stack>
        )}

        {!claims && showDetails && (
          <Text size="xs" c="red">
            Failed to decode JWT token. Check browser console.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
